import { CUSTOM_THEME_COLORS, type CustomThemeColor, type CustomThemeDTO, type ThemePreference } from '@clanfire/shared';

/**
 * Themes. Light and Dark are hand-tuned in index.css. Every other theme (the
 * Midnight and Ember presets, and your own) is eight colours, from which
 * deriveThemeVars works out the rest of the palette.
 */

export type PresetId = 'LIGHT' | 'DARK' | 'MIDNIGHT' | 'EMBER';

export const PRESETS: Record<PresetId, CustomThemeDTO & { label: string; description: string }> = {
  LIGHT: {
    label: 'Light',
    description: 'Cream and sand',
    base: 'LIGHT',
    colors: { background: '#f5ead8', sidebar: '#ebddc5', rail: '#3d472b', panel: '#eee7db', card: '#fbf6ee', text: '#201e1d', accent: '#a85a28', secondary: '#7a8a5e' },
  },
  DARK: {
    label: 'Dark',
    description: 'Warm charcoal',
    base: 'DARK',
    colors: { background: '#1b1916', sidebar: '#23201b', rail: '#262c1c', panel: '#201d19', card: '#2b2722', text: '#f5ead8', accent: '#d67f48', secondary: '#8fa073' },
  },
  MIDNIGHT: {
    label: 'Midnight',
    description: 'True black, easy on phones at night',
    base: 'DARK',
    colors: { background: '#000000', sidebar: '#0a0a0d', rail: '#000000', panel: '#07070a', card: '#16161c', text: '#e9e7f2', accent: '#8e9bff', secondary: '#6cc4b0' },
  },
  EMBER: {
    label: 'Ember',
    description: 'Glowing coals in ClanFire orange',
    base: 'DARK',
    colors: { background: '#1d120d', sidebar: '#27170f', rail: '#130a06', panel: '#21140e', card: '#311e15', text: '#fbe9dc', accent: '#ff7a45', secondary: '#f2b64a' },
  },
};

export const COLOR_INFO: Record<CustomThemeColor, { label: string; description: string }> = {
  background: { label: 'Chat background', description: 'Behind messages' },
  sidebar: { label: 'Channel list', description: 'Channels and DMs on the left' },
  rail: { label: 'Server list', description: 'The strip of server icons' },
  panel: { label: 'Member list', description: 'The panel on the right' },
  card: { label: 'Menus and pop-ups', description: 'Menus, profile cards and dialogs' },
  text: { label: 'Text', description: 'Messages and names' },
  accent: { label: 'Accent', description: 'Buttons, links and highlights' },
  secondary: { label: 'Second accent', description: 'Role tags and small details' },
};

/* ------------------------------------------------------------------ */
/* Colour maths                                                        */
/* ------------------------------------------------------------------ */

type RGB = [number, number, number];

function rgb(hex: string): RGB {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function hex([r, g, b]: RGB): string {
  return `#${[r, g, b].map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('')}`;
}

/** `a` moved `t` of the way towards `b`. */
export function mix(a: string, b: string, t: number): string {
  const [x, y] = [rgb(a), rgb(b)];
  return hex([x[0] + (y[0] - x[0]) * t, x[1] + (y[1] - x[1]) * t, x[2] + (y[2] - x[2]) * t]);
}

function alpha(color: string, a: number): string {
  const [r, g, b] = rgb(color);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function luminance(color: string): number {
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = rgb(color);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio, 1 to 21. Body text wants 4.5 or more. */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** Near-white or near-black, whichever reads better on `background`. */
function readableOn(background: string): string {
  return contrast('#ffffff', background) >= contrast('#141414', background) ? '#ffffff' : '#141414';
}

/** `color`, moved towards `toward` just far enough to reach `ratio` against `background`. */
function withContrast(color: string, background: string, ratio: number, toward: string): string {
  for (let t = 0; t <= 1; t += 0.05) {
    const candidate = mix(color, toward, t);
    if (contrast(candidate, background) >= ratio) return candidate;
  }
  return toward;
}

/* ------------------------------------------------------------------ */
/* Deriving a whole palette                                            */
/* ------------------------------------------------------------------ */

/** What stays the same for every theme on a light or dark base (status colours, shadows). */
const BASE_DEFAULTS = {
  LIGHT: {
    '--clanfire-danger': '#b3362b',
    '--clanfire-danger-soft': 'rgba(179, 54, 43, 0.1)',
    '--clanfire-success': '#56633f',
    '--clanfire-warning': '#8f5a0c',
    '--clanfire-online': '#5f8a3c',
    '--clanfire-idle': '#e08a4f',
    '--clanfire-dnd': '#c24a3a',
    '--clanfire-offline': '#8c8478',
    '--clanfire-shadow': '0 12px 32px rgba(46, 43, 37, 0.22), 0 2px 6px rgba(46, 43, 37, 0.1)',
  },
  DARK: {
    '--clanfire-danger': '#e0604c',
    '--clanfire-danger-soft': 'rgba(224, 96, 76, 0.14)',
    '--clanfire-success': '#aebf92',
    '--clanfire-warning': '#e0a84a',
    '--clanfire-online': '#9fb382',
    '--clanfire-idle': '#f6a06b',
    '--clanfire-dnd': '#e0604c',
    '--clanfire-offline': '#82796a',
    '--clanfire-shadow': '0 12px 36px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.35)',
  },
} as const;

/** Every colour token (the --clanfire-* properties in index.css) for a theme made of eight colours. */
export function deriveThemeVars(theme: CustomThemeDTO): Record<string, string> {
  const dark = theme.base === 'DARK';
  const { background, sidebar, rail, panel, card, text, accent, secondary } = theme.colors;
  const railFg = contrast(text, rail) >= 4.5 ? text : readableOn(rail);
  const roleTagBackground = mix(background, secondary, dark ? 0.2 : 0.22);
  return {
    '--clanfire-rail': rail,
    '--clanfire-rail-fg': railFg,
    '--clanfire-rail-muted': mix(railFg, rail, 0.4),
    '--clanfire-sidebar': sidebar,
    '--clanfire-panel': panel,
    '--clanfire-surface': background,
    '--clanfire-elevated': card,
    '--clanfire-sunken': mix(background, text, dark ? 0.07 : 0.06),
    '--clanfire-well': mix(sidebar, text, 0.1),
    '--clanfire-hover': alpha(text, dark ? 0.06 : 0.07),
    '--clanfire-active': alpha(text, dark ? 0.11 : 0.12),
    '--clanfire-border': alpha(text, dark ? 0.1 : 0.14),
    '--clanfire-border-strong': alpha(text, dark ? 0.18 : 0.26),
    '--clanfire-fg': text,
    '--clanfire-fg-soft': mix(text, background, 0.16),
    '--clanfire-fg-muted': withContrast(mix(text, background, 0.4), background, 4.5, text),
    '--clanfire-accent': accent,
    '--clanfire-accent-hover': mix(accent, dark ? '#ffffff' : '#000000', 0.12),
    '--clanfire-accent-fg': readableOn(accent),
    '--clanfire-accent-soft': alpha(accent, dark ? 0.14 : 0.1),
    '--clanfire-accent-2': secondary,
    '--clanfire-accent-2-soft': alpha(secondary, dark ? 0.2 : 0.22),
    '--clanfire-accent-2-ink': withContrast(secondary, roleTagBackground, 4.5, text),
    '--clanfire-link': withContrast(accent, background, 4.5, text),
    '--clanfire-mention': alpha(accent, 0.12),
    '--clanfire-mention-bar': accent,
    '--clanfire-scrollbar': mix(background, text, 0.25),
    ...BASE_DEFAULTS[theme.base],
  };
}

/** Things that will be hard to read in a theme, in plain words (empty: all good). */
export function readabilityIssues(theme: CustomThemeDTO): string[] {
  const { background, sidebar, panel, card, text, accent } = theme.colors;
  const issues: string[] = [];
  const places: Array<[string, string]> = [
    [background, 'the chat background'],
    [sidebar, 'the channel list'],
    [panel, 'the member list'],
    [card, 'menus and pop-ups'],
  ];
  for (const [color, where] of places) if (contrast(text, color) < 4.5) issues.push(`Text is hard to read on ${where}.`);
  if (contrast(accent, background) < 3) issues.push('The accent colour is hard to see on the chat background, so buttons and highlights may blend in.');
  return issues;
}

/* ------------------------------------------------------------------ */
/* Which theme is on                                                   */
/* ------------------------------------------------------------------ */

/**
 * The colours a theme preference stands for: null for Light, Dark and System
 * (index.css has those), or the eight colours otherwise.
 */
export function themeColorsFor(theme: ThemePreference, custom: CustomThemeDTO | null): CustomThemeDTO | null {
  if (theme === 'MIDNIGHT' || theme === 'EMBER') return PRESETS[theme];
  if (theme === 'CUSTOM' && custom) return custom;
  return null;
}

/* ------------------------------------------------------------------ */
/* Theme codes                                                         */
/* ------------------------------------------------------------------ */

/** "cf1-d-" and the eight colours as hex, in CUSTOM_THEME_COLORS order. Short enough to paste in chat. */
export const THEME_CODE_RE = /\bcf1-([dl])((?:-[0-9a-f]{6}){8})\b/gi;

export function encodeThemeCode(theme: CustomThemeDTO): string {
  return `cf1-${theme.base === 'DARK' ? 'd' : 'l'}-${CUSTOM_THEME_COLORS.map((key) => theme.colors[key].slice(1).toLowerCase()).join('-')}`;
}

/** The theme in a code (surrounding spaces are fine), or null if it isn't one. */
export function decodeThemeCode(code: string): CustomThemeDTO | null {
  const match = /^cf1-([dl])((?:-[0-9a-f]{6}){8})$/i.exec(code.trim());
  if (!match) return null;
  const parts = match[2]!.slice(1).split('-');
  const colors = Object.fromEntries(CUSTOM_THEME_COLORS.map((key, i) => [key, `#${parts[i]!.toLowerCase()}`])) as CustomThemeDTO['colors'];
  return { base: match[1]!.toLowerCase() === 'd' ? 'DARK' : 'LIGHT', colors };
}

/** Theme codes in a message, at most a few, without repeats. */
export function extractThemeCodes(content: string, max = 2): string[] {
  const found = new Set<string>();
  for (const m of content.matchAll(THEME_CODE_RE)) {
    found.add(m[0].toLowerCase());
    if (found.size >= max) break;
  }
  return [...found];
}
